import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Check, Zap, ArrowRight, Shield, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import EvStation from '@mui/icons-material/EvStation';

export default function VelkommenEVSignup() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    await base44.entities.ForcedSwitchEVSignup.create({ email, source_page: 'velkommen' });
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <section className="py-16 md:py-20 bg-[#1a3a32] text-white">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full mb-4">
              <span className="text-xs font-semibold text-gray-200">Frivillige fordele, hvis de passer til dig</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full mb-6">
              <EvStation sx={{ fontSize: 18, color: '#eab308' }} />
              <span className="text-sm font-semibold text-[#eab308]">GRATIS FOR ALLE ELBIL-EJERE</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Har du elbil? Så har du ekstra fordele hos enkel
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto mb-4">
              Hvis du er blevet kunde hos enkel energi og har elbil med en kompatibel ladeboks (Zaptec, NexBlue eller Easee), kan du få adgang til alle vores EV-fordele – helt gratis og uden ekstra abonnement. Tilmeld dig herunder.
            </p>
            <p className="text-xs text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Dette er et frivilligt tilvalg. Det påvirker ikke din ret til at opsige den tildelte aftale med 1 måneds varsel til udgangen af en måned. Læs vilkår og priser, før du tilmelder dig.
            </p>
          </div>

          {/* 4 EV benefit cards */}
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 ring-1 ring-[#eab308]/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-[#eab308] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#1a3a32]" />
                </div>
                <div>
                  <span className="bg-[#eab308] text-gray-900 px-2 py-0.5 rounded text-[10px] font-bold mr-2">PRISGARANTI</span>
                  <span className="font-bold text-white">Saving Mode</span>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Du betaler altid for de <strong className="text-white">billigste timer</strong> i din ladeperiode – uanset hvornår vi oplader. Estimeret besparelse op til ca. 30% vs. almindelig opladning.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-5 border border-white/10 ring-1 ring-[#eab308]/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-[#eab308] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-[#1a3a32]" />
                </div>
                <div>
                  <span className="bg-[#eab308] text-gray-900 px-2 py-0.5 rounded text-[10px] font-bold mr-2">LADERABAT</span>
                  <span className="font-bold text-white">10 øre af spotprisen</span>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Oveni prisgarantien får du <strong className="text-white">0,10 kr./kWh rabat inkl. moms</strong> fratrukket automatisk på din el-regning. Gælder al opladning via enkel Saving Mode app.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-[#eab308] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#1a3a32]" />
                </div>
                <span className="font-bold text-white">Serviceaftale – 0 kr./md.</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Inkluderer Saving Mode, laderabat, support, fejlfinding og <strong className="text-white">automatisk elafgift-refusion</strong>. Se de fulde vilkår for serviceaftalen, før du tilmelder dig.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-[#eab308] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-[#1a3a32]" />
                </div>
                <span className="font-bold text-white">enkel Saving Mode app</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Planlæg opladning, sæt afgangstider, følg forbrug og besparelser. Automatisk opladning i billigste timer – du behøver ikke tænke over det.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <a href="https://apps.apple.com/dk/app/enkel-smart-charge/id6503988043" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                  <img src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg" alt="App Store" className="h-8" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.gadgetlab.smartcharge" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-8" />
                </a>
              </div>
            </div>
          </div>

          {/* Compatible chargers */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-8">
            <h4 className="font-bold text-white mb-3 text-sm">Kompatible ladebokse</h4>
            <div className="grid grid-cols-3 gap-2">
              {['Zaptec', 'NexBlue', 'Easee'].map((name, idx) => (
                <div key={idx} className="bg-white/10 px-3 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-2">
                  <Check className="w-3 h-3 text-[#eab308] flex-shrink-0" />
                  <span>{name}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Har du en ladeboks fra et af disse mærker og er blevet kunde hos enkel? Tilmeld dig herunder, og vi aktiverer dine fordele.
            </p>
          </div>

          {/* Signup form */}
          <div className="bg-white/10 border border-white/20 rounded-2xl p-6 md:p-8 max-w-lg mx-auto">
            {submitted ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-[#eab308] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-[#1a3a32]" />
                </div>
                <h3 className="text-xl font-bold mb-2">Tak! Vi har registreret dig</h3>
                <p className="text-gray-300 text-sm">Vi sørger for at aktivere din gratis serviceaftale, Saving Mode, prisgaranti og laderabat hurtigst muligt.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-center mb-2">Tilmeld dig gratis EV-fordele</h3>
                <p className="text-gray-300 text-sm text-center mb-6">
                  Indtast den email, du normalt modtager dine el-regninger på, så aktiverer vi serviceaftale, Saving Mode, prisgaranti og laderabat.
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="din@email.dk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-12"
                  />
                  <Button type="submit" disabled={loading} className="bg-[#eab308] hover:bg-[#ca9a06] text-gray-900 font-semibold h-12 px-6 whitespace-nowrap">
                    {loading ? 'Sender...' : 'Tilmeld mig'}
                  </Button>
                </form>
              </>
            )}
          </div>

          {/* Links to learn more */}
          <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
            <Link to="/portal/smart-charge" className="text-[#eab308] hover:underline font-medium flex items-center gap-1">
              Læs om Saving Mode <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/el-fordele#laderabat" className="text-[#eab308] hover:underline font-medium flex items-center gap-1">
              Beregn din laderabat <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/el-fordele#serviceaftale" className="text-[#eab308] hover:underline font-medium flex items-center gap-1">
              Se serviceaftale <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}